<script setup>
import { ref, watch } from 'vue';
import { Form } from 'ant-design-vue';

const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: '新增'
    },
    formItems: {
        type: Array,
        required: true
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:open', 'submit']);

const formRef = ref();
const formData = ref({});

// 初始化表單資料
const initializeFormData = () => {
    const initialData = {};
    props.formItems.forEach((item) => {
        initialData[item.key] = '';
    });
    formData.value = initialData;
};

// 監聽 open 狀態，重置表單
watch(
    () => props.open,
    (newVal) => {
        if (newVal) {
            resetForm();
        }
    }
);

// 監聽 formItems 變化，重新初始化
watch(
    () => props.formItems,
    () => {
        initializeFormData();
    },
    { immediate: true }
);

// 重置表單
const resetForm = () => {
    // 初始化所有表單項目為空字串
    const initialData = {};
    props.formItems.forEach((item) => {
        initialData[item.key] = '';
    });
    formData.value = initialData;
    formRef.value?.resetFields();
};

// 處理表單提交
const handleSubmit = async () => {
    try {
        await formRef.value.validate();
        emit('submit', formData.value);
    } catch (error) {
        console.error('表單驗證失敗:', error);
    }
};

// 處理取消
const handleCancel = () => {
    emit('update:open', false);
    resetForm();
};

// 處理表單值變更
const handleFormChange = (changedValues, allValues) => {
    // 確保所有表單項目都存在
    const updatedData = { ...formData.value, ...allValues };
    formData.value = updatedData;
};
</script>

<template>
    <a-modal
        :open="open"
        :title="title"
        wrap-class-name="edit-modal"
        :confirm-loading="loading"
        @ok="handleSubmit"
        @cancel="handleCancel"
        :width="520"
        :mask-closable="false"
        :footer="null"
        :centered="true"
    >
        <a-form
            ref="formRef"
            layout="vertical"
            class="base-edit-form"
            :model="formData"
            @change="handleFormChange"
        >
            <template v-for="item in formItems" :key="item.key">
                <a-form-item
                    :label="item.label"
                    :name="item.key"
                    :rules="item.rules || []"
                    class="!mb-3"
                >
                    <a-input
                        v-if="!item.type || item.type === 'input'"
                        v-model:value="formData[item.key]"
                        :placeholder="item.placeholder || '請輸入'"
                        :type="item.inputType || 'text'"
                        allow-clear
                        size="middle"
                        class="w-full"
                    />
                    <a-select
                        v-else-if="item.type === 'select'"
                        v-model:value="formData[item.key]"
                        :placeholder="item.placeholder || '請選擇'"
                        allow-clear
                        size="middle"
                        class="w-full"
                    >
                        <a-select-option
                            v-for="option in item.options"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </a-select-option>
                    </a-select>
                    <a-textarea
                        v-else-if="item.type === 'textarea'"
                        v-model:value="formData[item.key]"
                        :placeholder="item.placeholder || '請輸入'"
                        :rows="item.rows || 3"
                        allow-clear
                        size="middle"
                        class="w-full"
                    />
                </a-form-item>
            </template>
        </a-form>

        <div class="base-edit-form__footer">
            <a-button @click="handleCancel">取消</a-button>
            <a-button type="primary" @click="handleSubmit" :loading="loading">
                確認
            </a-button>
        </div>
    </a-modal>
</template>

<style scoped>
.base-edit-form :deep(.ant-form-item-label) {
    padding-bottom: 4px;
}

.base-edit-form :deep(.ant-form-item-label > label) {
    font-size: 13px;
    height: auto;
}

.base-edit-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 8px;
    padding-top: 14px;
    border-top: 1px solid #eef2f0;
}
</style>
